/* ---------------------------------------------------------------------------
   Pakistan, Frontier Works Organization — the third department, and the first
   with no register in it.

   Added for the two concept notes put to FWO on 7 September 2026: a construction
   progress system for one new bridge, and a lifecycle inventory for the existing
   bridges on one of FWO's BOT motorways. Both need this deployment to be able to
   BE Pakistan before either can be shown, and neither has a bridge list yet.

   ── THERE IS NO BUNDLED REGISTER, AND THAT IS THE ONLY HONEST STATE ──

   FWO has not named the motorway and has not supplied a bridge list. Proposal 2
   puts it at "approximately 20-50 bridges, to be confirmed by FWO" — which is
   the client's own wording for *we have not been told yet*. So there is no seed
   for this tenant, `seedFor('pk-fwo')` returns [], and every screen says the
   register is empty and where that answer came from.

   The alternative was to seed it with the two Karachi structures whose drawings
   are in the project folder (Haider Chowk Flyover, Labaik Chowk Flyover, tender
   drawings by a consultant, client not named). They are not FWO bridges, they
   are not on any BOT motorway, and bundling them to stop a screen looking bare
   would be exactly the kind of plausible-looking filler this register refuses
   everywhere else. A register invented to fill a screen is worse than an empty
   one that says so.

   WHAT THIS SECTION SAYS ABOUT THE SEED IS STILL TRUE; WHAT IT ONCE IMPLIED
   ABOUT D1 IS NOT. Those two structures were entered into the database by hand
   on 2026-09-17, as BR-KHI-001 and BR-KHI-002, and a signed-in session
   therefore reads two records rather than none. That is a decision taken in the
   application rather than in this file, and nothing here overrides it — the seed
   is a fallback, D1 is the register. It is recorded here because the paragraph
   above reads like a claim that no such record exists anywhere, and it is not
   one: it is a claim about what this build BUNDLES.

   That makes this the first tenant to exercise the empty-register path at all.
   `hasSeed` has existed since tenancy did and nothing had ever been drawn
   without one — see `TN-19` in scripts/smoke.ts, which now renders every screen
   for a department with nothing in it.

   ── WHAT IS SOURCED AND WHAT IS PROVISIONAL ──

   SOURCED. The department's own name and the Consultancy directorate are from
   the two concept notes as submitted ("Frontier Works Organization",
   "Brig. Dr. Adeel Zafar, Director Consultancy"). `ministry` is the Pakistan
   Army: FWO is described as a military engineering organisation of the Army
   rather than as sitting under a civilian ministry, so the slot named `ministry`
   in `AgencyBlock` carries the body it actually sits under rather than a
   ministry invented to fill a field labelled one. The outline and bounds are
   Natural Earth's Pakistan point-of-view edition — src/data/pakistan.ts is the
   whole of that argument and should be read before anybody touches the map.

   PROVISIONAL, AND FLAGGED HERE BECAUSE A LETTERHEAD IS NOT A THING TO GUESS AT.
   `unit`, `unitShort`, the vocabulary below and `chainage` are this project's
   best reading of Pakistani motorway practice, not FWO's own documents. They
   print on an inventory sheet somebody files. Confirm all of them against one
   FWO drawing and one FWO inventory form before this is shown to the client, and
   treat a correction as a one-line change rather than a finding.

   NOTHING HERE IS ENGINEERING. Same rule as the other two profiles: the element
   list, the condition scales, the screening and the quantity methods are the
   same for every department. What differs is what the letterhead says, what the
   columns are called, and what a rupee is.
   --------------------------------------------------------------------------- */

import { PAKISTAN_BOUNDS, PAKISTAN_OUTLINE } from '../../data/pakistan.ts';
/* TEMPORARY — the demonstration register. Delete with src/data/fwo-demo.ts. */
import { demoEnabled } from '../../data/fwo-demo.ts';
import { classifyRoad } from '../roads.ts';
import type { RoadClassRule, TenantProfile } from '../types.ts';

/**
 * Pakistan's road classification, from the code a route carries.
 *
 * The National Highway Authority's own lettering: `M` motorways, `N` national
 * highways, `E` expressways, `S` strategic roads. An FWO BOT motorway is an
 * M-number, which is why this is here at all and is not `[]` as DPWH's is.
 *
 * UNVERIFIED AGAINST ANY FWO RECORD, because there is no record to verify it
 * against — this tenant's register is empty. That is a weaker footing than
 * Nepal's four prefixes, which were checked against five transcribed structures.
 * The failure mode is mild and visible: `classifyRoad` returns null for anything
 * unmatched, so a code nobody anticipated groups as *not recorded* rather than
 * being bucketed into a class it does not belong to. Check these against FWO's
 * first bridge list and correct them there.
 *
 * Named rather than inlined so the `class` grouping below closes over the same
 * array the profile publishes — one list, no way for the two to disagree.
 */
const ROAD_CLASSES: readonly RoadClassRule[] = [
  { match: /^M-?\d/, label: 'Motorway' },
  { match: /^N-?\d/, label: 'National highway' },
  { match: /^E-?\d/, label: 'Expressway' },
  { match: /^S-?\d/, label: 'Strategic road' },
];

export const PK_FWO: TenantProfile = {
  id: 'pk-fwo',
  name: 'Pakistan — Frontier Works Organization',
  country: 'PK',
  countryName: 'Pakistan',

  agency: {
    government: 'Government of Pakistan',
    /* See the header: FWO is an engineering organisation of the Pakistan Army,
       not a department under a civilian ministry. This field carries the body it
       sits under rather than a ministry invented to fill a slot named one. */
    ministry: 'Pakistan Army',
    department: 'Frontier Works Organization',
    departmentShort: 'FWO',
    /* From the concept notes' own addressee line, "Director Consultancy". */
    branch: 'Consultancy Directorate',
    /* PROVISIONAL. Names the engagement rather than an FWO office, because the
       motorway has not been named — and says so, which is the same thing the
       register does with any other value nobody has supplied yet. */
    unit: 'BOT motorway bridge programme — motorway to be confirmed by FWO',
    unitShort: 'BOT motorway network',
    seal: 'PK',
  },

  brand: {
    emblems: [],
    logo: null,
    /* The flag's green and white. */
    ground: '#01411c',
    stripe: ['#ffffff', '#01411c'],
  },

  /* National bounds and no `home`. DPWH gets a corridor view because its
     register sits on three known highway sections; there is no corridor to open
     on here until FWO names the motorway, and a guessed home view would open the
     map somewhere with no reason to be there. The moment records carry
     coordinates, `fitHome` in src/map/nepalMap.ts fits them and this stops
     mattering. */
  geo: {
    bounds: PAKISTAN_BOUNDS,
    outline: PAKISTAN_OUTLINE,
    outlineProvenance:
      'Natural Earth 1:10m Admin 0 Countries, Pakistan point-of-view edition ' +
      '(ne_10m_admin_0_countries_pak), public domain, simplified to 652 points by ' +
      'Douglas–Peucker at a 0.0126° tolerance. A cartographic outline at country scale, ' +
      'not a survey boundary. The point-of-view edition differs from the default by up ' +
      'to about 380 km — see the header of src/data/pakistan.ts.',
  },

  /* PROVISIONAL — see the header. Pakistan's administrative units below the
     province are the district and, under it, the tehsil and the union council;
     `locality` takes the union council as the closest analogue to DoR's VDC and
     DPWH's barangay. `road` is Motorway rather than Highway because both concept
     notes are about a BOT motorway specifically; if FWO's register turns out to
     carry national highways too, this is the word that changes. */
  vocab: {
    area: 'District',
    areaPlural: 'Districts',
    region: 'Province',
    /* Deliberately generic. FWO's own term for the unit that maintains a
       structure is not known here, and inventing a proper noun for a field that
       prints on an inventory sheet is worse than a plain description. */
    office: 'Maintenance unit',
    locality: 'Union council',
    road: 'Motorway',
    chainage: 'Chainage',
  },

  /* PROVISIONAL. Pakistani motorway drawings mark a position as `123+450`, which
     is `station` here — the same rendering DPWH uses, off the same `Bridge.km`.
     `vocab.chainage` still reads "Chainage" because that is the word used for it;
     the two are independent, one being the label and the other the format. Check
     both against one FWO drawing. */
  chainage: 'station',

  roadClasses: ROAD_CLASSES,

  /* None, and that is the healthy state — see the note on `extras` in
     src/tenant/types.ts. Whatever columns FWO's own inventory form carries that
     `bridges` does not will land here once a form has been seen; declaring them
     ahead of that would be guessing at a sheet nobody has read. */
  extras: [],

  /* `class` leads, because on a motorway register the route is the one thing
     every structure will certainly have, where district and province have to be
     transcribed. All three produce nothing today, which is correct for a
     register with no records in it. */
  groupings: [
    { key: 'class', label: 'Road classification', of: (b) => classifyRoad(b.route, ROAD_CLASSES) },
    { key: 'district', label: 'District', of: (b) => b.district || null },
    { key: 'province', label: 'Province', of: (b) => b.province || null },
  ],

  currency: {
    code: 'PKR',
    symbol: 'Rs',
    unit: 'rupee',
    /* Pakistan's fiscal year runs July to June and is written as a pair. */
    fiscalPeriodLabel: 'Fiscal year, July to June (e.g. 2026-27)',
  },

  /* English only, as DPWH is. FWO's correspondence and both concept notes are in
     English, and Urdu is not half-translated here on the strength of a guess —
     see `locales` in src/tenant/types.ts, and the header of src/data/locale.ts
     on what a partial translation has to be able to say about itself. */
  locales: ['en'],

  /* NULL, DELIBERATELY, and for a different reason than DPWH's null.
     Pakistan does have published seismic zoning, and that is precisely why
     nothing is said here: Nepal's note works because NBC 105:2020 leaves no
     low-hazard zone anywhere in the country, so a hazard screen could not
     separate its structures even with the map. That sentence is not true of
     Pakistan, whose zoning does include low-hazard areas — so restating Nepal's
     with the country name swapped would invert its meaning. The general caveat
     the screen falls back to (the zoning factor is jurisdiction-level data the
     register does not hold, so what is ranked is vulnerability) is true here
     without any of this. Fill it in from the code itself or leave it null. */
  seismicZoningNote: null,

  /* GETTERS, AND ONLY BECAUSE OF THE DEMONSTRATION. Both would be plain strings
     but for src/data/fwo-demo.ts: while that data is in the build these two
     sentences are the ONLY thing on several screens telling a reader that the
     records in front of them were invented, and a static string would go on
     saying the register is empty over twenty-three visible structures. A profile that
     contradicts its own register is the exact failure this field exists to
     prevent. Read at call time so the flag decides, the way `bundled` in
     ../seeds.ts does. Delete the demonstration branches with that file and put
     the two plain strings back. */
  get registerProvenanceShort(): string {
    return demoEnabled()
      ? 'Demonstration data — not FWO’s register'
      : 'No structures yet — awaiting FWO’s bridge list';
  },

  get registerProvenance(): string {
    if (demoEnabled()) {
      return (
        'DEMONSTRATION DATA, NOT A REGISTER. Nine of the twenty-three structures on screen ' +
        'are real bridges in Pakistan by name, watercourse, locality and province, taken from ' +
        'an encyclopaedia article. The other fourteen are on the M-1 motorway, which is real, ' +
        'as are the districts and the rivers, canal and railway they cross — but no source ' +
        'names the bridges on that road, so what they are CALLED here is for demonstration too. ' +
        'Everything else about all twenty-three is demonstration data: the asset ids, the chainages, the ' +
        'dimensions, the deck and flood levels, every inventory-sheet field, and every ' +
        'inspection, condition rating and issue. Nothing here was measured, surveyed or ' +
        'inspected, and no figure on any of these screens may be quoted. It is in the build so ' +
        'the application can be shown working while FWO’s own bridge list is outstanding, and ' +
        'it comes out when that list arrives. Every value carries a “D” mark on the inventory ' +
        'sheet for the same reason.'
      );
    }
    return (
      'Empty. This department has been configured so the application can be shown as ' +
      'Pakistan, but FWO has not yet named the BOT motorway or supplied its bridge list — ' +
      'Proposal 2 of 7 September 2026 puts it at approximately 20 to 50 structures, to be ' +
      'confirmed. Nothing is seeded: the two Karachi flyovers whose tender drawings are in ' +
      'the project folder are not FWO structures and are not on any motorway, and a register ' +
      'made up to fill a screen would be worse than an empty one that says so.'
    );
  },

  /* PROVISIONAL (LC-24). FWO has stated no re-rating interval; five years,
     and two for a posted bridge. */
  rerating: { years: 5, restrictedYears: 2 },

  /* PROVISIONAL (LC-61). Annual routine and five-yearly principal, as the
     cycle always ran; six months and three years for a restricted bridge or
     one whose latest condition is 3 of 4 or worse. */
  inspection: { routineMonths: 12, principalMonths: 60, shortRoutineMonths: 6, shortPrincipalMonths: 36, poorFrom: 0.75 },
};
